<script setup lang="ts">
    import { useSpeechRecognition } from '~/composables/useSpeechRecognition';

    defineProps<{
        isProcessing: boolean;
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        send: [text: string];
        stop: [];
    }>();

    const { isSupported: micSupported, isRecording, isTranscribing, transcript, preconnect, disconnect, start: startRecording, stop: stopRecording } = useSpeechRecognition();

    onMounted(() => preconnect());
    onBeforeUnmount(() => disconnect());

    const inputText = ref('');
    const textareaRef = ref<HTMLTextAreaElement | null>(null);

    const micBusy = computed(() => isRecording.value || isTranscribing.value);

    const autoResize = () => {
        const el = textareaRef.value;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    };

    watch(inputText, () => nextTick(autoResize));

    watch(transcript, (val) => {
        if (val && (isRecording.value || isTranscribing.value)) {
            inputText.value = val;
        }
    });

    watch(isTranscribing, (transcribing, wasTranscribing) => {
        if (wasTranscribing && !transcribing && transcript.value) {
            inputText.value = transcript.value;
        }
    });

    const handleSend = () => {
        const text = inputText.value.trim();
        if (!text) return;

        inputText.value = '';
        emit('send', text);
    };

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const toggleMic = async () => {
        if (isRecording.value) {
            await stopRecording();
        } else {
            await startRecording();
        }
    };

    const submitText = (text: string) => {
        inputText.value = text;
        nextTick(handleSend);
    };

    defineExpose({ submitText });
</script>

<template>
    <div class="shrink-0">
        <div class="bg-white border border-[#E7E7E7] rounded-[12px] shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-[14px]">
            <textarea
                ref="textareaRef"
                v-model="inputText"
                rows="2"
                class="w-full resize-none text-[13px] leading-[1.2] placeholder:text-gray-400 text-gray-900 font-inter bg-transparent focus:outline-none disabled:opacity-50 p-0 block max-h-[350px] overflow-y-auto"
                :placeholder="$t('ai.chat.placeholder')"
                :disabled="micBusy"
                @keydown="handleKeydown"
            />
            <div class="flex items-center justify-end gap-[10px] mt-2">

                <!-- Mic: recording state (red pulsing, stop icon) — disabled until Deepgram scoped tokens are implemented -->
                <!-- <button
                    v-if="isRecording"
                    class="w-[37px] h-[37px] rounded-full bg-red-500 border border-red-500 ring-2 ring-red-300 animate-pulse-ring shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] flex items-center justify-center transition-all"
                    @click="toggleMic"
                >
                    <i class="fa-solid fa-stop text-[10px] text-white"></i>
                </button> -->

                <!-- Mic: transcribing state (spinner) — disabled until Deepgram scoped tokens are implemented -->
                <!-- <button
                    v-else-if="isTranscribing"
                    class="w-[37px] h-[37px] rounded-full bg-branding-brand-yellow border border-branding-brand-yellow shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] flex items-center justify-center"
                    disabled
                >
                    <i class="fa-solid fa-spinner fa-spin text-[12px] text-branding-rich-black"></i>
                </button> -->

                <!-- Stop button (processing) -->
                <button
                    v-if="isProcessing"
                    type="button"
                    class="w-[34px] h-[34px] rounded-full bg-gray-900 hover:bg-gray-700 border border-gray-900 shadow-[0_1px_3px_rgba(0,0,0,0.1)] flex items-center justify-center transition-colors cursor-pointer"
                    @click="emit('stop')"
                >
                    <i class="fa-solid fa-stop text-[10px] text-white"></i>
                </button>

                <!-- Send button (yellow paper-plane) -->
                <button
                    v-else
                    type="button"
                    class="w-[34px] h-[34px] rounded-full bg-branding-brand-yellow hover:bg-branding-mustard border border-[#E8B102] shadow-[0_1px_3px_rgba(0,0,0,0.1)] flex items-center justify-center transition-colors cursor-pointer"
                    @click="handleSend"
                >
                    <i class="fa-solid fa-paper-plane text-[13px] text-black"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
    @keyframes pulse-ring {
        0%,
        100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5);
        }
        50% {
            box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
        }
    }
    .animate-pulse-ring {
        animation: pulse-ring 1.5s ease-in-out infinite;
    }
</style>
